/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { CommandBarButton, IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Checkbox, ICheckboxStyleProps, ICheckboxStyles } from '@fluentui/react/lib/Checkbox';
import { ChoiceGroup, IChoiceGroupOption, IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles } from '@fluentui/react/lib/ChoiceGroup';
import { DirectionalHint } from '@fluentui/react/lib/ContextualMenu';
import { Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import { IStackProps, IStackSlots, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { ITextProps, ITextStyles } from '@fluentui/react/lib/Text';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { LuisDebouncedCollapsableSearchBox } from 'src/core/components/LuisDebouncedCollapsableSearchBox';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { RenameModal } from 'src/core/components/RenameModal';
import { ShimmerFactory } from 'src/core/components/ShimmerFactory';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { logButtonClick, logButtonSuccessfulClick, logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { getCommandBarButtonStyles } from 'src/core/utils/azureRbacStyles';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { pivotKeyToPivotTitle } from 'src/core/utils/telemetryUtils';
import { CustomTextClassData, CustomTextClassStore } from 'src/customText/core/stores/CustomTextClassStore';
import { CustomTextGptPredictionsStore } from 'src/customText/core/stores/CustomTextGptPredictionsStore';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { customTextTeachingBubbleMessages } from 'src/customText/core/utils/customTextTeachingBubbleMessages';
import { createTeachingBubble, dequeueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { PalettePivotType } from 'src/verticals/EntityPalette';

type CustomTextClassPaletteProps = {
    id?: string;
    isLoading?: boolean;
    autoLabelButtonId?: string;
    headerElement?: JSX.Element;
    footerElement?: JSX.Element;
    isTaggingDisabled?: boolean;
    isAddClassDisabled?: boolean;
    openPivot?: PalettePivotType;
    OnAutoLabelClick?: () => void;
    classes: CustomTextClassStore[];
    checkDeleteClass?: () => boolean;
    projectKind: CustomTextProjectKind;
    classLabels?: CustomTextClassData[];
    tagsPivotHeaderElement?: JSX.Element;
    onAddClass: (className: string) => void;
    onDeleteClass: (classId: string) => void;
    onRenderAzureMLTab: () => React.ReactNode;
    isTriggerAutoTaggingJobDisabled?: boolean;
    gptStoreHouse?: CustomTextGptPredictionsStore;
    onSubmitClasses: (classIds: string[]) => void;
    onRenderDistributionTab: () => React.ReactNode;
    onRenderRecommendationTab: () => React.ReactNode;
    onOpenPivotChange?: (newPivot: PalettePivotType) => void;
};

const messages = defineMessages({
    yes: { id: 'customText.customTextClassPalette.yes', defaultMessage: 'Yes' },
    delete: { id: 'customText.customTextClassPalette.delete', defaultMessage: 'Delete' },
    title: { id: 'customText.customTextClassPalette.title', defaultMessage: 'Activity pane' },
    addClass: { id: 'customText.customTextClassPalette.addClass', defaultMessage: 'Add class' },
    autoLabel: { id: 'customText.customTextClassPalette.autoLabel', defaultMessage: 'Auto-label' },
    tagsTabHeader: { id: 'customText.customTextClassPalette.tagsTabHeader', defaultMessage: 'Labels' },
    labeledByGpt: { id: 'customText.customTextClassPalette.labeledByGpt', defaultMessage: 'Label suggested by AI' },
    emptySearchMsg: { id: 'customText.customTextClassPalette.emptySearchMsg', defaultMessage: 'No classes found.' },
    addClassModalTitle: { id: 'customText.customTextClassPalette.addClassModalTitle', defaultMessage: 'Class name' },
    labelingWithGpt: { id: 'customText.customTextClassPalette.labelingWithGpt', defaultMessage: 'Labeling with GPT' },
    deleteModalTitle: { id: 'customText.customTextClassPalette.deleteModalTitle', defaultMessage: 'Delete selected class' },
    distributionTabHeader: { id: 'customText.customTextClassPalette.distributionTabHeader', defaultMessage: 'Distribution' },
    azureMLTabHeader: { id: 'customText.customTextClassPalette.azureMLTabHeader', defaultMessage: 'Azure Machine Learning' },
    emptyStateMsg: { id: 'customText.customTextClassPalette.emptyStateMsg', defaultMessage: "You haven't created any classes." },
    disabledTooltipMsg: { id: 'customText.customTextClassPalette.disabledTooltipMsg', defaultMessage: 'Please select document(s).' },
    recommendationsTabHeader: { id: 'customText.customTextClassPalette.recommendationsTabHeader', defaultMessage: 'Recommendations' },
    searchFieldPlaceholder: { id: 'customText.customTextClassPalette.searchFieldPlaceholder', defaultMessage: 'Search for a class...' },
    deleteModalDescription: {
        id: 'customText.customTextClassPalette.deleteModalDescription',
        defaultMessage: 'Are you sure you want to delete "{className}" class?'
    },
    teachingBubbleTitle: {
        id: 'customText.customTextClassPalette.teachingBubbleTitle',
        defaultMessage: 'Check out labeling with Azure Machine Learning '
    },
    teachingBubbleContent: {
        id: 'customText.customTextClassPalette.teachingBubbleContent',
        defaultMessage: 'You can connect your Custom named entity recognition project with Azure Machine Learning to use its labeling tools'
    }
});

const PalettePadding = '15px';
const scopedTheme = getScopedTheme('CustomTextClassPalette');
const { styleComponent } = getStylistV2('CustomTextClassPalette');
const Loading = styleComponent<IStackProps>(Stack)('Loading', { padding: 12 });
const Pane = styleComponent<IStackProps>(Stack)('Pane', theme => ({
    width: '100%',
    height: '100%',
    borderLeft: theme.borderSpecs
}));
const ToolBar = styleComponent<IStackProps>(Stack)('ToolBar', theme => ({
    height: '42px',
    margin: '0px 10px 0 0',
    borderBottom: `solid 1px ${theme.separatorColor}`
}));
const emptyMsgStyles = classNamesFunction<ITextProps, ITextStyles>()({
    root: { fontSize: '14px', textAlign: 'center', fontStyle: 'italic' }
});
const gptLabelStyles = (padding: string) =>
    classNamesFunction<ITextProps, ITextStyles>()({ root: { fontStyle: 'italic', fontSize: '12px', paddingLeft: padding } });
const addClassButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    root: { paddingLeft: PalettePadding, minWidth: '116px' }
});
const smallScreenPanelContainer = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: { overflowY: 'auto !important' }
});
const choiceGroupStyles = classNamesFunction<IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles>()({
    choiceFieldWrapper: { width: '100%' }
});
const gptCheckboxStyles = classNamesFunction<ICheckboxStyleProps, ICheckboxStyles>()({
    checkbox: { backgroundColor: `${scopedTheme.gptLabelColor} !important` },
    checkmark: { opacity: 1, color: `${scopedTheme.gptCheckBoxMarkColor} !important` }
});
const gptChoiceGroupStyles = classNamesFunction<IChoiceGroupOptionStyleProps, IChoiceGroupOptionStyles>()({
    field: {
        '::after': {
            top: '5px',
            left: '5px',
            width: '10px',
            height: '10px',
            backgroundColor: `${scopedTheme.gptLabelColor} !important`
        }
    }
});

export const CustomTextClassPalette = observer((props: CustomTextClassPaletteProps) => {
    const {
        classes,
        isLoading,
        onAddClass,
        projectKind,
        id = getId(),
        headerElement,
        gptStoreHouse,
        footerElement,
        onDeleteClass,
        onSubmitClasses,
        classLabels = [],
        checkDeleteClass,
        OnAutoLabelClick,
        onOpenPivotChange,
        openPivot = 'tags',
        onRenderAzureMLTab,
        tagsPivotHeaderElement,
        onRenderDistributionTab,
        onRenderRecommendationTab,
        isTaggingDisabled = false,
        isAddClassDisabled = false,
        autoLabelButtonId = getId(),
        isTriggerAutoTaggingJobDisabled = false
    } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, uiStore, rootStore } = useGlobalState();
    const { isLargeOrGreater, isXLargeOrGreater } = useResponsiveMode();
    const { isLabelingLocked, currentDocumentStore } = rootStore.currentCustomTextProjectStore;
    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const addClassButtonIdRef = React.useRef<string>(getId());

    const [query, setQuery] = React.useState('');
    const [pivotTabsDisplay, setPivotTabsDisplay] = React.useState('block');
    const [isAddNewClassModalHidden, setIsAddNewClassModalHidden] = React.useState(true);
    const [selectedPivot, setSelectedPivot] = React.useState<PalettePivotType>(openPivot);
    const [classToBeDeleted, setClassToBeDeleted] = React.useState<CustomTextClassStore>(null);

    const isInEmptyState = classes.length === 0;
    const filteredClasses = substringSearchStrategy(classes, query, 'name');
    const gptPredictionOfCurrentDoc = gptStoreHouse?.documents?.find(d => d.name === currentDocumentStore?.name);

    React.useEffect(() => {
        if (openPivot !== selectedPivot) {
            setSelectedPivot(openPivot);
        }
    }, [openPivot]);

    React.useEffect(() => {
        if (isLargeOrGreater) {
            setPivotTabsDisplay('block');
        } else {
            setPivotTabsDisplay('grid');
        }
    }, [isLargeOrGreater]);

    React.useEffect(() => () => dequeueTeachingBubble('CustomTextAddClass'), []);

    React.useEffect(() => {
        if (
            uiStore.teachingBubbles.CustomTextDataTaggingPalette &&
            !uiStore.teachingBubbles.CustomTextAddClass &&
            !uiStore.skippedTeachingBubbles.CustomTextDataTaggingPalette
        ) {
            createTeachingBubble(
                'CustomTextAddClass',
                localize(customTextTeachingBubbleMessages.addClassButtonBody),
                localize(customTextTeachingBubbleMessages.addClassButtonTitle),
                `#${addClassButtonIdRef.current}`,
                true,
                true
            );
        }
    }, [Object.values(uiStore.teachingBubbles)]);

    const handleSubmitDeleteClass = () => {
        onDeleteClass(classToBeDeleted.id);
        setClassToBeDeleted(null);
        logButtonSuccessfulClick(serviceName, 'DataLabeling', 'DeleteClass', 'Labels', rootStore.currentCustomTextProjectStore.id);
    };

    const handleSubmitAddClass = (dto: { name: string }) => {
        onAddClass(dto.name);
        setIsAddNewClassModalHidden(true);
        logButtonSuccessfulClick(serviceName, 'DataLabeling', 'AddClass', 'Labels', rootStore.currentCustomTextProjectStore.id);
    };

    const handlePivotChange = (newPivotKey: PalettePivotType) => {
        logPivotClick(serviceName, 'DataLabeling', pivotKeyToPivotTitle(newPivotKey), rootStore.currentCustomTextProjectStore.id);
        setSelectedPivot(newPivotKey);
        onOpenPivotChange?.(newPivotKey);
    };

    const handleAddClass = () => {
        logButtonClick(serviceName, 'DataLabeling', 'AddClass', 'Labels', rootStore.currentCustomTextProjectStore.id);
        setIsAddNewClassModalHidden(false);
    };

    const handleDeleteClass = (cls: CustomTextClassStore) => {
        if (checkDeleteClass()) {
            setClassToBeDeleted(cls);
        }
        logButtonClick(serviceName, 'DataLabeling', 'DeleteClass', 'Labels', rootStore.currentCustomTextProjectStore.id);
    };

    const handleMultiClassChange = (classId: string, isChecked: boolean) =>
        isChecked
            ? onSubmitClasses([...classLabels.map(c => c.id), classId])
            : onSubmitClasses(classLabels.filter(l => l.id !== classId).map(cls => cls.id));

    const getMoreOptionsButton = (cls: CustomTextClassStore) => (
        <RBACTooltip
            hidden={canWrite}
            displayedUserRole={displayedUserRole}
            data-automation-id="moreButtonLanguageToolTip"
            componentSurroundedByToolTip={
                <IconButton
                    disabled={isLabelingLocked || !canWrite}
                    iconProps={{ iconName: 'delete' }}
                    title={localize(messages.delete)}
                    onClick={() => handleDeleteClass(cls)}
                />
            }
        />
    );

    const renderGptOptions = (cls: CustomTextClassStore) => {
        if (gptPredictionOfCurrentDoc?.classLabels.some(c => c.id === cls.id)) {
            return (
                <Stack horizontal>
                    <IconButton
                        iconProps={{ iconName: 'CheckMark' }}
                        styles={{ root: { color: scopedTheme.gptAcceptColor } }}
                        onClick={() => dispatcher.dispatch('submitGptClassificationLabels', { classIds: [cls.id] })}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Blocked' }}
                        styles={{ root: { color: scopedTheme.gptRejectColor } }}
                        onClick={() => dispatcher.dispatch('rejectGptClassificationLabels', { classIds: [cls.id] })}
                    />
                </Stack>
            );
        }
    };

    const renderGptLabel = (cls: CustomTextClassStore, padding: string) => {
        if (gptPredictionOfCurrentDoc?.classLabels.some(c => c.id === cls.id)) {
            return <LuisText styles={gptLabelStyles(padding)}>{localize(messages.labeledByGpt)}</LuisText>;
        }
    };

    const singleClassificationChoiceGroupOptions: IChoiceGroupOption[] = filteredClasses.map(cls => ({
        key: cls.id,
        text: cls.name,
        disabled: !canWrite,
        ariaLabel: cls.name,
        styles: mergeStyleSets(
            choiceGroupStyles,
            gptPredictionOfCurrentDoc?.classLabels.some(c => c.id === cls.id) && gptChoiceGroupStyles
        ),
        onClick: () => onSubmitClasses(classLabels?.[0]?.id === cls.id ? [] : [cls.id]),
        onRenderField: (rendererProps, renderer) => (
            <Stack>
                <Stack
                    horizontal
                    verticalFill
                    key={cls.id}
                    verticalAlign="center"
                    horizontalAlign="space-between"
                    styles={{ root: { width: '100%' } }}>
                    <RBACTooltip
                        hidden={canWrite}
                        displayedUserRole={displayedUserRole}
                        data-automation-id="classLabelLanguageToolTip"
                        componentSurroundedByToolTip={
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                {renderer(rendererProps)}
                                {renderGptOptions(cls)}
                            </Stack>
                        }
                    />

                    {getMoreOptionsButton(cls)}
                </Stack>
                {renderGptLabel(cls, '26px')}
            </Stack>
        )
    }));

    const SingleClassificationPalette = () => (
        <LuisTooltip content={isTaggingDisabled && localize(messages.disabledTooltipMsg)} directionalHint={DirectionalHint.leftCenter}>
            <ChoiceGroup
                selectedKey={classLabels?.[0]?.id}
                disabled={isLabelingLocked || isTaggingDisabled}
                options={singleClassificationChoiceGroupOptions}
            />
        </LuisTooltip>
    );

    const MultiClassificationPalette = () => (
        <>
            {filteredClasses.map(cls => (
                <Stack>
                    <Stack key={cls.id} horizontal verticalAlign="center" horizontalAlign="space-between">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <RBACTooltip
                                hidden={canWrite}
                                displayedUserRole={displayedUserRole}
                                data-automation-id="classLabelLanguageToolTip"
                                componentSurroundedByToolTip={
                                    <LuisTooltip
                                        content={canWrite && isTaggingDisabled && localize(messages.disabledTooltipMsg)}
                                        directionalHint={DirectionalHint.leftCenter}>
                                        <Checkbox
                                            ariaLabel={cls.name}
                                            styles={gptPredictionOfCurrentDoc?.classLabels.some(c => c.id === cls.id) && gptCheckboxStyles}
                                            checked={classLabels.some(label => label.id === cls.id)}
                                            disabled={isLabelingLocked || !canWrite || isTaggingDisabled}
                                            onChange={(_, isChecked) => handleMultiClassChange(cls.id, isChecked)}
                                        />
                                    </LuisTooltip>
                                }
                            />
                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                                <LuisText variant="medium">{cls.name}</LuisText>
                                {renderGptOptions(cls)}
                            </Stack>
                        </Stack>

                        {getMoreOptionsButton(cls)}
                    </Stack>
                    {renderGptLabel(cls, '32px')}
                </Stack>
            ))}
        </>
    );

    const onRenderContent = () => {
        if (isInEmptyState) {
            return <LuisText styles={emptyMsgStyles}>{localize(messages.emptyStateMsg)}</LuisText>;
        }

        if (filteredClasses.length === 0) {
            return <LuisText styles={emptyMsgStyles}>{localize(messages.emptySearchMsg)}</LuisText>;
        }

        if (projectKind === 'CustomSingleLabelClassification') {
            return <SingleClassificationPalette />;
        }

        return <MultiClassificationPalette />;
    };

    const hideAmlTeachingBubble = () => {
        uiStore.setAmlTeachingBubbleHidden();
    };

    return (
        <Pane grow id={id} styles={!isXLargeOrGreater && smallScreenPanelContainer}>
            {isLoading ? (
                <Loading>
                    <ShimmerFactory shimmerCount={12} shimmerSpacing={15} fullWidth />
                </Loading>
            ) : (
                <Stack verticalFill tokens={{ childrenGap: 5 }} styles={{ root: { position: 'relative', marginBottom: '20px' } }}>
                    {headerElement}

                    <Pivot
                        selectedKey={selectedPivot}
                        styles={{ root: { marginLeft: PalettePadding, display: pivotTabsDisplay } }}
                        overflowBehavior={'menu'}
                        overflowAriaLabel={'overFlowPivotButton'}
                        onLinkClick={link => {
                            logPivotClick(
                                'classification',
                                'DataTagging',
                                link.props.itemKey as PalettePivotType,
                                rootStore.currentCustomTextProjectStore.id
                            );
                            handlePivotChange(link.props.itemKey as PalettePivotType);
                        }}>
                        <PivotItem itemKey="tags" headerText={localize(messages.tagsTabHeader)} />
                        <PivotItem itemKey="distribution" headerText={localize(messages.distributionTabHeader)} />
                        <PivotItem itemKey="recommendations" headerText={localize(messages.recommendationsTabHeader)} />

                        {FeatureToggles.AMLIntegration ? (
                            <PivotItem itemKey="azureML" headerText={localize(messages.azureMLTabHeader)} />
                        ) : null}
                    </Pivot>

                    {!uiStore.isAmlTeachingBubbleHidden && (
                        <TeachingBubble
                            calloutProps={{ directionalHint: DirectionalHint.bottomCenter }}
                            target={'.ms-Pivot-link[aria-label="overFlowPivotButton"]'}
                            isWide
                            hasCloseButton
                            closeButtonAriaLabel="Close"
                            onDismiss={hideAmlTeachingBubble}
                            headline={localize(messages.teachingBubbleTitle)}>
                            {localize(messages.teachingBubbleContent)}
                        </TeachingBubble>
                    )}

                    {selectedPivot === 'tags' && [
                        <StackItem>
                            {tagsPivotHeaderElement}
                            <ToolBar horizontal verticalFill horizontalAlign="space-between">
                                <Stack horizontal verticalFill>
                                    <RBACTooltip
                                        hidden={canWrite || isAddClassDisabled}
                                        displayedUserRole={displayedUserRole}
                                        data-automation-id="addCTClassButtonLanguageToolTip"
                                        componentSurroundedByToolTip={
                                            <CommandBarButton
                                                onClick={handleAddClass}
                                                iconProps={{ iconName: 'Add' }}
                                                id={addClassButtonIdRef.current}
                                                disabled={isLabelingLocked || !canWrite || isAddClassDisabled}
                                                styles={mergeStyleSets(addClassButtonStyles, getCommandBarButtonStyles(false))}>
                                                {localize(messages.addClass)}
                                            </CommandBarButton>
                                        }
                                    />
                                    {OnAutoLabelClick && (
                                        <RBACTooltip
                                            hidden={canWrite}
                                            displayedUserRole={displayedUserRole}
                                            data-automation-id="startAutoTaggingJobButtonLanguageToolTip"
                                            componentSurroundedByToolTip={
                                                <CommandBarButton
                                                    role="menuitem"
                                                    id={autoLabelButtonId}
                                                    onClick={OnAutoLabelClick}
                                                    text={localize(messages.autoLabel)}
                                                    name={localize(messages.autoLabel)}
                                                    iconProps={{ iconName: 'AutoEnhanceOn' }}
                                                    styles={getCommandBarButtonStyles(false)}
                                                    disabled={!canWrite || isTriggerAutoTaggingJobDisabled || isLabelingLocked}
                                                />
                                            }
                                        />
                                    )}
                                </Stack>

                                <LuisDebouncedCollapsableSearchBox
                                    size={18}
                                    value={query}
                                    disabled={isLabelingLocked || isInEmptyState}
                                    ariaLabel={localize(messages.searchFieldPlaceholder)}
                                    placeholder={localize(messages.searchFieldPlaceholder)}
                                    onChange={(_, newValue?: string) => setQuery(newValue)}
                                />
                            </ToolBar>
                        </StackItem>,
                        ...(gptStoreHouse?.tracker?.isRunning
                            ? [
                                  <Stack horizontal tokens={{ childrenGap: 8, padding: '2px 15px' }}>
                                      <Spinner size={SpinnerSize.xSmall} />
                                      <LuisText color={scopedTheme.gptTextColor}>{localize(messages.labelingWithGpt)}</LuisText>
                                  </Stack>
                              ]
                            : []),
                        <Stack
                            verticalFill
                            tokens={{ padding: `2px ${PalettePadding}`, childrenGap: 10 }}
                            styles={{ root: { overflow: 'auto', minHeight: 100, position: 'relative' } }}
                            verticalAlign={isInEmptyState || filteredClasses.length === 0 ? 'center' : 'start'}>
                            {onRenderContent()}
                        </Stack>,
                        footerElement
                    ]}

                    {selectedPivot === 'distribution' && onRenderDistributionTab()}
                    {selectedPivot === 'recommendations' && onRenderRecommendationTab()}
                    {selectedPivot === 'azureML' && onRenderAzureMLTab()}
                </Stack>
            )}

            <DeletePromptModal
                hidden={!classToBeDeleted}
                onDelete={handleSubmitDeleteClass}
                submitButtonText={localize(messages.yes)}
                onDismiss={() => setClassToBeDeleted(null)}
                dialogContentProps={{ title: localize(messages.deleteModalTitle) }}>
                {localize(messages.deleteModalDescription, { className: classToBeDeleted?.name })}
            </DeletePromptModal>

            <RenameModal
                name=""
                onSubmit={handleSubmitAddClass}
                hidden={isAddNewClassModalHidden}
                nameMaxLength={LuisConstants.CustomText.maxNamesCharLength}
                existingEntityNames={classes.map(c => c.name)}
                onDismiss={() => setIsAddNewClassModalHidden(true)}
                dialogContentProps={{ title: localize(messages.addClassModalTitle) }}
            />
        </Pane>
    );
});
